import { useLayoutEffect, useMemo, useRef, useState } from "react";

import { Badge } from "@/ui";
import {
  isMatchLive,
  useBracketMatches,
  useTournament,
  type BracketMatch,
  type BracketTeam,
  type TournamentFormat,
} from "@/features/queries";

export function BracketView({ tournamentId }: { tournamentId: number }) {
  const { data: matches = [], isLoading } = useBracketMatches(tournamentId);
  const { data: tournament } = useTournament(tournamentId);

  if (isLoading) {
    return <p className="body" style={{ color: "var(--fg2)" }}>Загрузка сетки…</p>;
  }

  if (matches.length === 0) {
    return (
      <p className="body" style={{ color: "var(--fg2)" }}>
        Сетка ещё не сгенерирована.
      </p>
    );
  }

  return <BracketLayout matches={matches} format={tournament?.format} />;
}

/** Picks the right visual for the format:
 *   - SE → single bracket tree
 *   - DE → stacked upper + lower + grand-final
 *   - RR → standings table + matches-by-round list
 *
 * Walkover / BYE-seeded matches are filtered out for SE/DE so the
 * bracket reads Challonge-style: the player who would have "won" a BYE
 * simply appears for the first time in the next round, with no empty
 * placeholder match drawn for them. RR genuinely has no BYEs to hide.
 */
export function BracketLayout({
  matches,
  format,
}: {
  matches: BracketMatch[];
  format?: TournamentFormat;
}) {
  if (format === "round_robin") {
    return <RoundRobinView matches={matches} />;
  }

  const visible = matches.filter(isPlayableMatch);
  const upper = visible.filter((m) => m.bracket === "upper");
  const lower = visible.filter((m) => m.bracket === "lower");
  const gf = visible.filter((m) => m.bracket === "grand_final");

  const isDouble = lower.length > 0 || gf.length > 0;
  if (!isDouble) {
    return <BracketTree matches={upper} />;
  }

  const upperLabeler = (round: number, total: number) => {
    const remaining = total - round;
    if (remaining === 0) return "Верхний финал";
    if (remaining === 1) return "Верхний полуфинал";
    return `Верхний раунд ${round}`;
  };
  const lowerLabeler = (round: number, total: number) => {
    if (round === total) return "Нижний финал";
    return `Нижний раунд ${round}`;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Subsection title="Верхняя сетка">
        <BracketTree matches={upper} labelRound={upperLabeler} />
      </Subsection>
      {lower.length > 0 && (
        <Subsection title="Нижняя сетка">
          <BracketTree matches={lower} labelRound={lowerLabeler} />
        </Subsection>
      )}
      {gf.length > 0 && (
        <Subsection title="Гранд-финал">
          <div style={{ maxWidth: 280 }}>
            <MatchCard match={gf[0]!} />
          </div>
        </Subsection>
      )}
    </div>
  );
}

/** A match is "playable" if both sides are real teams and it isn't an
 *  auto-resolved walkover. Walkovers from BYE seeding (or DE phantom
 *  cascades) shouldn't render — the surviving player visually starts
 *  in the next round. */
function isPlayableMatch(m: BracketMatch): boolean {
  if (m.state === "walkover") return false;
  if (m.team_a?.is_bye || m.team_b?.is_bye) return false;
  return true;
}

function Subsection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="section-header" style={{ padding: "0 0 6px" }}>{title}</div>
      {children}
    </div>
  );
}

// ---------- Round-robin --------------------------------------------------

interface Standing {
  team: BracketTeam;
  played: number;
  won: number;
  lost: number;
  points: number;
}

function RoundRobinView({ matches }: { matches: BracketMatch[] }) {
  const standings = useMemo(() => computeStandings(matches), [matches]);

  const byRound = useMemo(() => {
    const g = new Map<number, BracketMatch[]>();
    for (const m of matches) {
      const list = g.get(m.round) ?? [];
      list.push(m);
      g.set(m.round, list);
    }
    for (const [, list] of g) list.sort((a, b) => a.slot - b.slot);
    return [...g.entries()].sort((a, b) => a[0] - b[0]);
  }, [matches]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Subsection title="Турнирная таблица">
        <StandingsTable rows={standings} />
      </Subsection>
      <Subsection title="Матчи">
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {byRound.map(([round, list]) => (
            <div key={round}>
              <div className="caption" style={{ color: "var(--fg2)", marginBottom: 4 }}>
                Тур {round}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {list.map((m) => (
                  <MatchCard key={m.id} match={m} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </Subsection>
    </div>
  );
}

function StandingsTable({ rows }: { rows: Standing[] }) {
  if (rows.length === 0) {
    return <p className="body" style={{ color: "var(--fg2)" }}>Пока пусто.</p>;
  }
  return (
    <div
      style={{
        background: "var(--bg-section)",
        border: "1px solid var(--separator)",
        borderRadius: "var(--r-md)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "32px 1fr 40px 40px 40px 48px",
          padding: "8px 12px",
          gap: 8,
          background: "var(--bg-secondary)",
          fontSize: 12,
          fontWeight: 600,
          color: "var(--fg3)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        <div>#</div>
        <div>Команда</div>
        <div style={{ textAlign: "center" }}>И</div>
        <div style={{ textAlign: "center" }}>В</div>
        <div style={{ textAlign: "center" }}>П</div>
        <div style={{ textAlign: "center" }}>Очки</div>
      </div>
      {rows.map((r, idx) => (
        <div
          key={r.team.id}
          style={{
            display: "grid",
            gridTemplateColumns: "32px 1fr 40px 40px 40px 48px",
            padding: "10px 12px",
            gap: 8,
            borderTop: idx === 0 ? "none" : "1px solid var(--separator)",
            fontSize: 14,
            alignItems: "center",
          }}
        >
          <div style={{ color: "var(--fg2)", fontWeight: 600 }}>{idx + 1}</div>
          <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {r.team.name}
          </div>
          <div style={{ textAlign: "center", color: "var(--fg2)" }}>{r.played}</div>
          <div style={{ textAlign: "center", color: "var(--brand-orange)", fontWeight: 600 }}>{r.won}</div>
          <div style={{ textAlign: "center", color: "var(--fg2)" }}>{r.lost}</div>
          <div
            style={{
              textAlign: "center",
              fontFamily: "var(--font-display)",
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            {r.points}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Count wins/losses per team from confirmed/walkover matches. */
function computeStandings(matches: BracketMatch[]): Standing[] {
  const byTeam = new Map<number, Standing>();
  const ensure = (t: BracketTeam): Standing => {
    const prev = byTeam.get(t.id);
    if (prev) return prev;
    const s: Standing = { team: t, played: 0, won: 0, lost: 0, points: 0 };
    byTeam.set(t.id, s);
    return s;
  };
  for (const m of matches) {
    if (!m.team_a || !m.team_b || m.team_a.is_bye || m.team_b.is_bye) continue;
    ensure(m.team_a);
    ensure(m.team_b);
    if (m.state !== "confirmed" && m.state !== "walkover") continue;
    if (m.winner_team_id == null) continue;
    const winner = m.winner_team_id === m.team_a.id ? m.team_a : m.team_b;
    const loser = winner.id === m.team_a.id ? m.team_b : m.team_a;
    const w = ensure(winner);
    const l = ensure(loser);
    w.played += 1;
    w.won += 1;
    w.points += 1;
    l.played += 1;
    l.lost += 1;
  }
  return [...byTeam.values()].sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    if (b.won !== a.won) return b.won - a.won;
    return (a.team.name ?? "").localeCompare(b.team.name ?? "");
  });
}

/** Challonge-style left-to-right bracket with SVG connectors.
 *  `labelRound` overrides the default "Финал/Полуфинал" labels — useful for
 *  the lower bracket of a double-elim, where the SE naming doesn't apply. */
export function BracketTree({
  matches,
  labelRound,
}: {
  matches: BracketMatch[];
  labelRound?: (round: number, total: number) => string;
}) {
  const byRound = useMemo(() => {
    const g = new Map<number, BracketMatch[]>();
    for (const m of matches) {
      const list = g.get(m.round) ?? [];
      list.push(m);
      g.set(m.round, list);
    }
    for (const [, list] of g) list.sort((a, b) => a.slot - b.slot);
    return [...g.entries()].sort((a, b) => a[0] - b[0]);
  }, [matches]);

  const totalRounds = byRound.length;
  const containerRef = useRef<HTMLDivElement>(null);
  const matchRefs = useRef<Map<number, HTMLDivElement | null>>(new Map());
  const [paths, setPaths] = useState<string[]>([]);
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const compute = () => {
      const crect = container.getBoundingClientRect();
      setSize({ w: crect.width, h: crect.height });
      const out: string[] = [];
      for (const m of matches) {
        if (m.next_match_id == null) continue;
        const child = matchRefs.current.get(m.id);
        const parent = matchRefs.current.get(m.next_match_id);
        if (!child || !parent) continue;
        const cr = child.getBoundingClientRect();
        const pr = parent.getBoundingClientRect();
        const x1 = cr.right - crect.left;
        const y1 = cr.top + cr.height / 2 - crect.top;
        const x2 = pr.left - crect.left;
        const y2 = pr.top + pr.height / 2 - crect.top;
        const mid = x1 + (x2 - x1) / 2;
        out.push(`M ${x1} ${y1} H ${mid} V ${y2} H ${x2}`);
      }
      setPaths(out);
    };

    compute();
    const obs = new ResizeObserver(compute);
    obs.observe(container);
    return () => obs.disconnect();
  }, [matches]);

  return (
    <div style={{ overflowX: "auto", paddingBottom: 8 }}>
      <div
        ref={containerRef}
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "stretch",
          gap: 40,
          padding: "8px 4px",
          minWidth: "100%",
        }}
      >
        <svg
          width={size.w}
          height={size.h}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            pointerEvents: "none",
            overflow: "visible",
          }}
        >
          {paths.map((d, i) => (
            <path
              key={i}
              d={d}
              stroke="var(--separator)"
              strokeWidth={1.5}
              fill="none"
            />
          ))}
        </svg>
        {byRound.map(([round, roundMatches]) => (
          <div
            key={round}
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-around",
              gap: 12,
              minWidth: 200,
            }}
          >
            <div className="section-header" style={{ textAlign: "center" }}>
              {(labelRound ?? roundLabel)(round, totalRounds)}
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-around",
                flex: 1,
                gap: 12,
              }}
            >
              {roundMatches.map((m) => (
                <div
                  key={m.id}
                  ref={(el) => {
                    matchRefs.current.set(m.id, el);
                  }}
                >
                  <MatchCard match={m} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MatchCard({ match }: { match: BracketMatch }) {
  const winnerA = match.winner_team_id === match.team_a?.id;
  const winnerB = match.winner_team_id === match.team_b?.id;
  const live = isMatchLive(match);

  const scoreFor = (side: "a" | "b") =>
    match.games.length > 0
      ? match.games.reduce(
          (n, g) => n + (side === "a" ? (g.a > g.b ? 1 : 0) : (g.b > g.a ? 1 : 0)),
          0,
        )
      : null;

  return (
    <div
      style={{
        background: live ? "rgba(30,122,90,0.10)" : "var(--bg-section)",
        border: `1px solid ${live ? "#1E7A5A" : "var(--separator)"}`,
        borderRadius: "var(--r-md)",
        overflow: "hidden",
        minWidth: 180,
        boxShadow: live ? "0 0 0 2px rgba(30,122,90,0.25)" : undefined,
      }}
    >
      <Side team={match.team_a} winner={winnerA} score={scoreFor("a")} />
      <div style={{ height: 1, background: "var(--separator)" }} />
      <Side team={match.team_b} winner={winnerB} score={scoreFor("b")} />
      {(live || match.state === "waiting_confirm" || match.state === "disputed") && (
        <div style={{ padding: "4px 10px", borderTop: "1px solid var(--separator)" }}>
          {live && <Badge kind="success">идёт</Badge>}
          {!live && match.state === "waiting_confirm" && <Badge kind="warning">ждёт подтв.</Badge>}
          {!live && match.state === "disputed" && <Badge kind="live">спор</Badge>}
        </div>
      )}
    </div>
  );
}

function Side({
  team,
  winner,
  score,
}: {
  team: BracketTeam | null;
  winner: boolean;
  score: number | null;
}) {
  const label = !team ? (
    <span style={{ color: "var(--fg3)" }}>TBD</span>
  ) : team.is_bye ? (
    <span style={{ color: "var(--fg3)" }}>BYE</span>
  ) : (
    <span
      style={{
        fontWeight: winner ? 600 : 500,
        color: winner ? "var(--brand-orange)" : "var(--fg1)",
      }}
    >
      {team.name}
    </span>
  );

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "8px 12px",
        background: winner ? "rgba(255,90,31,0.06)" : "transparent",
        gap: 8,
        minHeight: 34,
      }}
    >
      <div style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label}
      </div>
      {score !== null && (
        <div
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: 15,
            color: winner ? "var(--brand-orange)" : "var(--fg2)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {score}
        </div>
      )}
    </div>
  );
}

function roundLabel(round: number, total: number): string {
  const remaining = total - round;
  if (remaining === 0) return "Финал";
  if (remaining === 1) return "Полуфинал";
  if (remaining === 2) return "Четвертьфинал";
  return `Раунд ${round}`;
}
